import { useEffect, useRef } from 'react';



import { useReactive } from 'ahooks';
import { Avatar, Button, Col, DatePicker, Dropdown, Input, List, Modal, Popconfirm, Row, Select, Space } from 'antd';
import type { MenuProps } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import dayjs from 'dayjs';

const { TextArea } = Input;
import _, { find, isEmpty, map, orderBy, unionWith } from 'lodash';
import moment, { Moment } from 'moment';
import { TWorkerPosition } from 'types/worker';



import { BruhTimePicker } from '@atoms/bruhTimePicker';
import { RowSkeleton } from '@atoms/skeletons/rowSkeleton';
import { Common } from '@classes/commonMethods';
import { CRMAPIManager } from '@classes/crmApiManager';
import { SettingsManager } from '@classes/settingsManager';
import { SkeletonWrapper } from '@common/SkeletonWrapper/SkeletonWrapper';
import { faCaretDown, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { store as ServiceStore } from '@organisms/service/serviceStore';
import { imagePicker } from '@utils/imagePicker';



import { Conflict, TScheduleException, TScheduleExceptionStatus, TScheduleExceptionType } from '../../common/types';



import './exceptionModal.scss';
import { formatAndLocaleDate } from '@organisms/orders/orderProfile/common/helpers/formatAndLocaleDate';


export type TimePeriod = {
    from?: moment.Moment;
    /**
     * Разница в минутах
     */
    duration?: number;
    to?: moment.Moment;
};

type TProps = {
    exceptionEditMode: boolean;
    exceptionModal: boolean;
    curException: TScheduleException;
    exceptionStatuses: TScheduleExceptionStatus[];
    exceptionTypes: TScheduleExceptionType[];
    availablePeriods: TimePeriod[];
    onClose: () => void;
    onDelete: () => void;
    onSave: () => void;
    setCurException: (exc: TScheduleException) => TScheduleException;
    setDurationPickerVisible: (val: boolean) => void;
    messageApi: MessageInstance;
    conflicts: Conflict[];
    isLoading?: boolean;
    isCrmSchedule?: boolean;
    workerPositions?: TWorkerPosition[];
    selectedWorkerPositions?: TWorkerPosition[];
    onWorkerPositionSelect?: (item: TWorkerPosition) => void;
};

const ScheduleExceptionModal = ({
    exceptionEditMode,
    exceptionModal,
    curException,
    exceptionStatuses,
    exceptionTypes,
    availablePeriods,
    onClose,
    onDelete,
    onSave,
    setCurException,
    setDurationPickerVisible,
    messageApi,
    conflicts,
    isLoading,
    isCrmSchedule,
    workerPositions = [],
    selectedWorkerPositions = [],
    onWorkerPositionSelect,
}: TProps): JSX.Element => {
    const state = useReactive({
        exceptionChanged: false,
        reasonOpen: false,
        periodOpen: false,
        startTimeError: false,
        startDateError: false,
        durationError: false,
        endTimeError: false,
        endDateError: false,
        worker_positions: [] as TWorkerPosition[],
        searchValue: null,
        isWorkerPositionsModalOpen: false,
    });
    const reasonRef = useRef(null);

    useEffect(() => {
        if (!exceptionModal) {
            state.startDateError = false;
            state.startTimeError = false;
            state.durationError = false;
            state.endDateError = false;
            state.endTimeError = false;
            state.exceptionChanged = false;
        }

        if (exceptionEditMode) return;
    }, [exceptionModal]);

    useEffect(() => {
        if (curException == null) return;
        state.exceptionChanged = true;
    }, [curException?.duration]);

    // Проверки на соответствие доступным периодам и ограничениям типа
    useEffect(() => {
        if (curException == null) return;
        if (
            curException?.duration > curException?.type?.max_duration ||
            curException?.duration < curException?.type?.min_duration
        ) {
            state.durationError = true;
        } else state.durationError = false;
    }, [curException?.duration, curException?.type, exceptionModal]);

    function toMoment(date: string = '01-01-2025', time: string = '00:00'): moment.Moment {
        return moment(`${date} ${time}:00`, 'DD-MM-YYYY HH:mm:ss');
    }

    function toDayjs(date: string = '01-01-2025', time: string = '00:00') {
        return dayjs(`${date} ${time}:00`, 'DD-MM-YYYY HH:mm:ss');
    }

    function handleExceptionTypeChange(value: TScheduleExceptionType) {
        let tDuration =
            curException.duration <= value.min_duration ||
            curException.duration >= value.max_duration
                ? value.min_duration
                : curException.duration;
        if (value.is_daily_boundary && tDuration % (24 * 60) != 0) tDuration = value.min_duration;
        let tStartDate = curException.start_date;
        let tStartTime = curException.start_time;
        let tStart = toMoment(tStartDate, tStartTime);
        if (value.is_daily_boundary && tStart != tStart.clone().startOf('day')) {
            tStartDate = tStart.format('DD-MM-YYYY');
            tStartTime = tStart.format('HH:mm');
        }
        let tStop = tStart.clone().add(tDuration, 'minute');
        setCurException({
            ...curException,
            type: value,
            duration: tDuration,
            start_date: tStartDate,
            start_time: tStartTime,
            end_date: tStop.format('DD-MM-YYYY'),
            end_time: tStop.format('HH:mm'),
        });
    }

    function filteredPeriods(
        periods: TimePeriod[],
        exceptionType: TScheduleExceptionType
    ): TimePeriod[] {
        if (curException == null) return [];
        return periods.filter((p) => {
            if (exceptionType?.is_daily_boundary) {
                const startDay =
                    p.from == p.from.clone().startOf('day')
                        ? p.from.clone()
                        : p.from.clone().add(1, 'day').startOf('day');
                if (
                    p.to != null &&
                    typeof p.to != 'string' &&
                    p.to.diff(startDay, 'minutes') < exceptionType.min_duration
                )
                    return false;
            }
            if (p.duration != null && p.duration < exceptionType?.min_duration) return false;
            return true;
        });
    }

    function makeItems(
        periods: TimePeriod[],
        exceptionType: TScheduleExceptionType
    ): MenuProps['items'] {
        if (curException == null) return [];
        const items: MenuProps['items'] = [];
        const tPeriods = filteredPeriods(periods, exceptionType);
        for (let i = 0; i < tPeriods.length; i++) {
            items.push({
                key: i,
                label: (
                    <>
                        {formatAndLocaleDate(tPeriods[i].from, true)}{' '}
                        {typeof tPeriods[i].to == 'string'
                            ? tPeriods[i].to
                            : 'по ' + formatAndLocaleDate(tPeriods[i].to, true)}
                    </>
                ),
            });
        }
        return items;
    }

    function handlePeriodSelection(info: { key: string; keyPath?: string[] }) {
        if (info.keyPath == undefined) return;
        // без типа считать нечего: границы периода берутся из его min_duration
        if (curException?.type == null) return;

        const periods = filteredPeriods(availablePeriods, curException.type);
        if (periods.length == 0) {
            //вряд ли, но на всякий случай надо
            messageApi.error('Нет подходящих периодов.');
            return;
        }

        const period = periods[Number(info.key)];
        if (curException.type.is_daily_boundary) {
            const startDay = period.from.clone();

            const stopDay = startDay.clone().add(curException.type.min_duration, 'minutes');
            setCurException({
                ...curException,
                start_date: startDay.format('DD-MM-YYYY'),
                start_time: startDay.format('HH:mm'),
                duration: curException.type.min_duration,
                end_date: stopDay.format('DD-MM-YYYY'),
                end_time: stopDay.format('HH:mm'),
            });
        } else {
            const stopDay = period.from.clone().add(curException.type.min_duration, 'minutes');
            setCurException({
                ...curException,
                start_date: period.from.clone().format('DD-MM-YYYY'),
                start_time: period.from.clone().format('HH:mm'),
                duration: curException.type.min_duration,
                end_date: stopDay.format('DD-MM-YYYY'),
                end_time: stopDay.format('HH:mm'),
            });
        }

        state.periodOpen = false;
    }

    function onWPDelete(item: TWorkerPosition) {
        setCurException({
            ...curException,
            worker_positions: curException.worker_positions.filter((wpi) => wpi.id != item.id),
        });
    }

    function onWPAdd(item: TWorkerPosition['id']) {
        const wp = state.worker_positions.find((wpi) => wpi.id == item);
        if (wp == undefined) return;
        setCurException({
            ...curException,
            worker_positions: curException.worker_positions.concat(wp),
        });
        state.searchValue = null;
        state.worker_positions = [];
    }

    async function handleSearch(value: string) {
        try {
            if (value.trim().length < 1) {
                state.worker_positions = [];
                return;
            }
            const creds = SettingsManager.getConnectionCredentials();
            const wpl = await CRMAPIManager.request<any>(async (api) => {
                return await api.search({
                    crm_id: creds?.crmID,
                    entityType: 'worker-position',
                    filter: {
                        deleted_at: 'null',
                        fromCreatedAt: null,
                        fromPrice: null,
                        fromUpdatedAt: null,
                        toCreatedAt: null,
                        toPrice: null,
                        toUpdatedAt: null,
                        sort: {
                            sort_by: 'name',
                            sort_dir: 'asc',
                        },
                    },
                    pagination: {
                        limit: 10,
                        page: 1,
                    },
                    term: value,
                });
            });
            state.worker_positions = wpl.data.data.map((si) => si.data);
        } catch (err) {
            console.log(err);
        }
    }

    return (
        <Modal
            className="schedule-exception-modal"
            title={`Изменение в графике ${exceptionEditMode ? '- редактирование' : '- добавление'}`}
            open={exceptionModal}
            onCancel={onClose}
            footer={
                curException?.status?.name == 'Ожидает' ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Popconfirm
                            title="Исключение будет удалено"
                            onConfirm={onDelete}
                            okText="Подтвердить"
                            cancelText="Отмена"
                            placement="topLeft"
                        >
                            <Button
                                danger
                                disabled={
                                    curException?.status?.name != 'Ожидает' ||
                                    curException.id == null
                                }
                            >
                                Удалить
                            </Button>
                        </Popconfirm>
                        <Space>
                            <Button onClick={onClose}>Отмена</Button>
                            <Button onClick={onSave} type="primary">
                                Сохранить
                            </Button>
                        </Space>
                    </div>
                ) : (
                    <Space>
                        <Button onClick={onClose}>Отмена</Button>
                        <Button disabled={state.durationError} onClick={onSave} type="primary">
                            Сохранить
                        </Button>
                    </Space>
                )
            }
        >
            <SkeletonWrapper isLoading={isLoading} skeleton={<RowSkeleton rowsCount={9} />}>
                <div className="exception-profile">
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Причина</label>
                        </Col>
                        <Col className="hpadded one-third-width">
                            <Select
                                disabled={curException?.status?.name != 'Ожидает'}
                                onChange={(value) => {
                                    handleExceptionTypeChange(
                                        exceptionTypes.find((eti) => eti.id == +value)
                                    );
                                    state.exceptionChanged = true;
                                }}
                                onClick={() => {
                                    state.reasonOpen = !state.reasonOpen;
                                }}
                                onSelect={() => {
                                    state.reasonOpen = true;

                                    if (!exceptionEditMode) state.periodOpen = true;
                                }}
                                open={state.reasonOpen}
                                options={Object.values(exceptionTypes).map((et) => {
                                    return { value: et.id, label: et.name };
                                })}
                                ref={reasonRef}
                                size="large"
                                style={{ width: '100%' }}
                                value={curException?.type?.name}
                            />
                        </Col>
                        <Col className="hpadded one-third-width">
                            <Dropdown
                                arrow={true}
                                disabled={
                                    availablePeriods.length == 0 ||
                                    exceptionEditMode ||
                                    curException?.status?.name != 'Ожидает'
                                }
                                menu={{
                                    items: makeItems(availablePeriods, curException?.type),
                                    onClick: handlePeriodSelection,
                                }}
                                onOpenChange={(open) => {
                                    if (open) state.reasonOpen = false;
                                    state.periodOpen = open;
                                }}
                                open={state.periodOpen}
                                placement="bottomRight"
                                trigger={['click']}
                            >
                                <a onClick={(e) => e.preventDefault()}>
                                    <Space>
                                        Свободные периоды
                                        <FontAwesomeIcon icon={faCaretDown} />
                                    </Space>
                                </a>
                            </Dropdown>
                        </Col>
                    </Row>
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Начало</label>
                        </Col>
                        <Col className="hpadded" style={{ maxWidth: '80px' }}>
                            {/* Выбор времени начала */}
                            <BruhTimePicker
                                closeOnSelect={true}
                                disabled={
                                    curException?.status?.name != 'Ожидает' ||
                                    curException?.type?.is_daily_boundary
                                }
                                format="HH:mm"
                                hasError={!isEmpty(conflicts)}
                                onChange={(val, _) => {
                                    const prevParts = curException.start_time
                                        .split(':')
                                        .map((s) => Number.parseInt(s));
                                    let prev = moment(curException.start_date, 'DD-MM-YYYY')
                                        .hours(prevParts[0])
                                        .minutes(prevParts[1]);
                                    let newStart = moment(curException.start_date, 'DD-MM-YYYY')
                                        .hours(val.hours())
                                        .minutes(val.minutes());
                                    if (newStart.isSameOrAfter(prev)) {
                                        let newEnd = newStart
                                            .clone()
                                            .add(curException.duration, 'minutes');
                                        setCurException({
                                            ...curException,
                                            start_time: val.format('HH:mm'),
                                            end_time: newEnd.format('HH:mm'),
                                        });
                                        state.exceptionChanged = true;
                                    } else {
                                        const exceptionEnd = moment(
                                            curException.end_date + 'T' + curException.end_time,
                                            'DD-MM-YYYYTHH:mm'
                                        );
                                        const newDuration = Math.trunc(
                                            moment
                                                .duration(exceptionEnd.diff(newStart))
                                                .as('minutes')
                                        );
                                        ServiceStore.setDuration(newDuration);
                                        setCurException({
                                            ...curException,
                                            start_time: val.format('HH:mm'),
                                            duration: newDuration,
                                        });
                                        state.exceptionChanged = true;
                                    }
                                }}
                                value={toMoment(curException?.start_date, curException?.start_time)}
                            />
                        </Col>
                        <Col className="hpadded" style={{ maxWidth: '130px' }}>
                            {/* Выбор даты начала */}
                            <DatePicker
                                allowClear={false}
                                disabled={curException?.status?.name != 'Ожидает'}
                                format="DD-MM-YYYY"
                                inputReadOnly
                                onChange={(_, dateString) => {
                                    const startDateTime = moment(
                                        `${dateString} ${curException.start_time}`,
                                        'DD-MM-YYYY HH:mm'
                                    );

                                    const endDateTime = startDateTime
                                        .clone()
                                        .add(curException.duration, 'minutes');

                                    setCurException({
                                        ...curException,
                                        start_date: startDateTime.format('DD-MM-YYYY'),
                                        start_time: startDateTime.format('HH:mm'),
                                        end_date: endDateTime.format('DD-MM-YYYY'),
                                        end_time: endDateTime.format('HH:mm'),
                                    });
                                }}
                                picker="date"
                                status={!isEmpty(conflicts) ? 'error' : ''}
                                value={toDayjs(curException?.start_date, curException?.start_time)}
                            />
                        </Col>
                    </Row>
                    {!isEmpty(conflicts) && (
                        <Row className="vcentered vpadded">
                            <Col className="hpadded one-third-width">Пояснение</Col>
                            <Col className="hpadded two-thirds-width">
                                <i>Пересекается с другим изменением</i>
                            </Col>
                        </Row>
                    )}
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Продолж-сть</label>
                        </Col>
                        <Col
                            className="hpadded two-thirds-width"
                            onClick={() => {
                                if (curException.status?.name == 'Завершено') return;
                                setDurationPickerVisible(true);
                            }}
                        >
                            <Input
                                disabled={curException?.status?.name == 'Завершено'}
                                status={state.durationError ? 'error' : ''}
                                value={Common.convertMinutes2Date(curException?.duration)}
                            />
                        </Col>
                    </Row>
                    {state.durationError && (
                        <Row className="vcentered vpadded">
                            <Col className="hpadded one-third-width">Пояснение</Col>
                            <Col className="hpadded two-thirds-width">
                                <i>
                                    {`${
                                        curException?.type?.name
                                    } занимает от ${Common.convertMinutes2Date(
                                        curException?.type?.min_duration
                                    )} ` +
                                        `до ${Common.convertMinutes2Date(
                                            curException?.type?.max_duration
                                        )}`}
                                </i>
                            </Col>
                        </Row>
                    )}
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Завершение</label>
                        </Col>
                        {/* Выбор времени завершения */}
                        <Col className="hpadded" style={{ maxWidth: '80px' }}>
                            <BruhTimePicker
                                closeOnSelect={true}
                                disabled={
                                    curException?.status?.name == 'Завершено' ||
                                    curException?.type?.is_daily_boundary
                                }
                                format="HH:mm"
                                hasError={!isEmpty(conflicts)}
                                onChange={(value: Moment, _) => {
                                    setCurException({
                                        ...curException,
                                        end_date: value.format('DD-MM-YYYY'),
                                        end_time: value.format('HH:mm'),
                                    });
                                    const startSplit = curException.start_time
                                        .split(':')
                                        .map((v) => Number(v));
                                    const start = moment(curException.start_date, 'DD-MM-YYYY')
                                        .hours(startSplit[0])
                                        .minutes(startSplit[1]);
                                    if (value.isSameOrBefore(start)) {
                                        const newStart = value
                                            .clone()
                                            .subtract(curException.duration, 'minutes');
                                        setCurException({
                                            ...curException,
                                            start_date: newStart.format('DD-MM-YYYY'),
                                            start_time: newStart.format('HH:mm'),
                                        });
                                        state.exceptionChanged = true;
                                    } else {
                                        const newDuration = Math.trunc(
                                            moment.duration(value.diff(start)).as('minutes')
                                        );
                                        ServiceStore.setDuration(newDuration);
                                        setCurException({ ...curException, duration: newDuration });
                                        state.exceptionChanged = true;
                                    }
                                }}
                                value={toMoment(curException?.end_date, curException?.end_time)}
                            />
                        </Col>
                        {/* Выбор даты завершения */}
                        <Col className="hpadded" style={{ maxWidth: '130px' }}>
                            <DatePicker
                                allowClear={false}
                                disabled={curException?.status?.name == 'Завершено'}
                                format="DD-MM-YYYY"
                                inputReadOnly
                                onChange={(_value, dateString) => {
                                    const endDateTime = moment(
                                        `${dateString} ${curException.end_time}`,
                                        'DD-MM-YYYY HH:mm'
                                    );

                                    const startDateTime = endDateTime
                                        .clone()
                                        .subtract(curException.duration, 'minutes');

                                    setCurException({
                                        ...curException,
                                        end_date: endDateTime.format('DD-MM-YYYY'),
                                        end_time: endDateTime.format('HH:mm'),
                                        start_date: startDateTime.format('DD-MM-YYYY'),
                                        start_time: startDateTime.format('HH:mm'),
                                    });
                                }}
                                picker="date"
                                status={!isEmpty(conflicts) ? 'error' : ''}
                                value={toDayjs(curException?.end_date, curException?.end_time)}
                            />
                        </Col>
                    </Row>
                    {!isEmpty(conflicts) && (
                        <Row className="vcentered vpadded">
                            <Col className="hpadded one-third-width">Пояснение</Col>
                            <Col className="hpadded two-thirds-width">
                                <i>Пересекается с другим изменением</i>
                            </Col>
                        </Row>
                    )}
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Комментарий</label>
                        </Col>
                        <Col className="hpadded two-thirds-width">
                            <TextArea
                                disabled={curException?.status?.name == 'Завершено'}
                                value={curException?.description}
                                maxLength={512}
                                onChange={(e) => {
                                    setCurException({
                                        ...curException,
                                        description: e.target.value,
                                    });
                                    state.exceptionChanged = true;
                                }}
                                placeholder="Отсутствует"
                                rows={4}
                                showCount
                            />
                        </Col>
                    </Row>
                    {/* <RenderIf condition={isCrmSchedule}>
                        <Row className="vcentered vpadded">
                            <Col className="hpadded one-third-width">
                                <label>Рабочие места</label>
                            </Col>
                            <Col className="hpadded two-thirds-width wp-list">
                                {curException?.status?.name == 'Ожидает' && (
                                    <Row className="search-field">
                                        <Select
                                            showSearch
                                            allowClear
                                            value={state.searchValue}
                                            placeholder="Поиск по имени"
                                            defaultActiveFirstOption={false}
                                            suffixIcon={null}
                                            filterOption={false}
                                            onSearch={handleSearch}
                                            onChange={(value) => onWPAdd(+value)}
                                            notFoundContent={null}
                                            options={state.worker_positions
                                                .filter(
                                                    (wp) =>
                                                        !curException?.worker_positions.includes(wp)
                                                )
                                                .map((wp) => ({
                                                    value: wp.id,
                                                    label: `#${wp.id}: ${wp.name}`,
                                                }))}
                                        />
                                    </Row>
                                )}
                                {curException?.worker_positions.length == 0 && (
                                    <Row>Список пуст</Row>
                                )}
                                {curException?.worker_positions.map((wpi) => (
                                    <Row className="wp-row">
                                        <Col>
                                            #{wpi.id}: {wpi.name}
                                        </Col>
                                        {curException?.status?.name == 'Ожидает' && (
                                            <Col>
                                                <Button
                                                    danger
                                                    className="hmargined delete-btn"
                                                    icon={<FontAwesomeIcon icon={faXmark} />}
                                                    onClick={() => onWPDelete(wpi)}
                                                    title="Удалить РМ"
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                ))}
                            </Col>
                        </Row>
                    </RenderIf> */}
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">
                            <label>Рабочие места</label>
                        </Col>
                        <Col className="hpadded two-thirds-width wp-list">
                            <Button
                                type="primary"
                                onClick={() => (state.isWorkerPositionsModalOpen = true)}
                            >
                                Изменить ({selectedWorkerPositions.length})
                            </Button>
                        </Col>
                    </Row>
                    <Row className="vcentered vpadded">
                        <Col className="hpadded one-third-width">Пересечения</Col>
                        <Col className="hpadded two-thirds-width">
                            {map(conflicts, (conflict) => (
                                <i>
                                    {conflict.title}
                                    <br />
                                </i>
                            ))}
                        </Col>
                    </Row>
                </div>
            </SkeletonWrapper>
            <Modal
                title="Рабочие места"
                open={state.isWorkerPositionsModalOpen}
                onCancel={() => (state.isWorkerPositionsModalOpen = false)}
                footer={[
                    <Button
                        type="primary"
                        onClick={() => (state.isWorkerPositionsModalOpen = false)}
                    >
                        Сохранить
                    </Button>,
                ]}
            >
                <List
                    className="customer-card-list"
                    dataSource={unionWith(
                        orderBy(selectedWorkerPositions, ['name'], ['asc']),
                        workerPositions,
                        (a, b) => a.id == b.id
                    )}
                    itemLayout="horizontal"
                    renderItem={(item) => (
                        <List.Item
                            className="customer-card"
                            key={item.id}
                            onClick={(e) => onWorkerPositionSelect(item)}
                        >
                            <Row style={{ width: '100%' }}>
                                <Col span={3}>
                                    <Avatar
                                        className="avatar"
                                        src={imagePicker({ picture: item?.picture })}
                                    />
                                </Col>
                                <Col span={18}>
                                    <Row>
                                        <Col>
                                            <h3 className="title">{item.name}</h3>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col span={3} className="button-select">
                                    <Button
                                        icon={<FontAwesomeIcon icon={faCheck} />}
                                        shape="circle"
                                        type={
                                            find(
                                                selectedWorkerPositions,
                                                (selectedWorker) => item?.id === selectedWorker?.id
                                            )
                                                ? 'primary'
                                                : 'default'
                                        }
                                    />
                                </Col>
                            </Row>
                        </List.Item>
                    )}
                />
            </Modal>
        </Modal>
    );
};

export { ScheduleExceptionModal };